if(!customElements.get('complete-set-component')){
  customElements.define(
    'complete-set-component',
    class CompleteSet extends HTMLElement {
        constructor() {
          super();
          this.initialized = false;
          
          this.state = {
            products: [],
            totals: { itemCount: 0, totalPrice: 0 },
            buttonText: 'ADD TO CART',
            initialCartState: [], // Array of variant IDs that were in cart on load
            hasAnyCompleteSetItemsInCart: false // Track if any complete set items exist in cart
          };
          
          // Event subscriptions
          this.cartUpdateUnsubscriber = null;
          this.boundCartHandler = null;
          
          // Loading state
          this.isLoading = false;
          
          this.cartSyncTimeout = null;
        }
      
        connectedCallback() {
          this.initializeCompleteSet();
    
          // Subscribe to cart updates for synchronization
          if (typeof subscribe === 'function') {
            this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
          }
    
          if(this.initialized && !this.boundCartHandler) {
              this.boundCartHandler = () => {
                  // Only re-fetch if we don't already have products loaded
                  // This prevents the component from disappearing when items are added to cart
                  if (this.state.products.length === 0) {
                      this.fetchRecommendations(this.dataset.productId, this.dataset.limit);
                  }
              };
              document.addEventListener('cart:recommendations-updated', this.boundCartHandler);
          }
        }
      
        async initializeCompleteSet() {
          this.initialized = true;
          await this.fetchRecommendations(this.dataset.productId, this.dataset.limit);
        }
      
        async fetchRecommendations(productId, limit) {
          try {
            // Storage key for this specific product's complete set
            const storageKey = `complete-set-${productId}`;
            
            // Fetch recommendations for product
            const response = await fetch(
              `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=complementary`
            );

            if (!response.ok) throw new Error('Network response was not ok');

            const data = await response.json();
            let products = data?.products || [];

            // Get stored products first
            const storedProducts = this.getStoredProducts(storageKey, productId);
            
            if (products.length === 0) {
              if (storedProducts.length > 0) {
                products = storedProducts;
              } else {
                return;
              }
            } else {
              // API returned products - merge with stored products to avoid losing any
              const mergedProducts = this.mergeProducts(products, storedProducts);
              
              this.storeProducts(storageKey, mergedProducts, productId);
              products = mergedProducts;
            }

            this.state.products = products.map(product => this.transformProduct(product));
      
            this.renderUI(this.state.products, products);
            this.setupEventListeners();

            this.dispatchEvent(new CustomEvent('add-ons-content-updated', {
              bubbles: true
            }));
            
            await this.checkInitialCartState();
          } catch (error) {
            console.error('Error handling recommendations:', error);
            
            // Fallback to stored products on API error
            const storageKey = `complete-set-${productId}`;
            const storedProducts = this.getStoredProducts(storageKey, productId);
            if (storedProducts.length > 0) {
              await this.initializeWithProducts(storedProducts);
            }
          }
        }
    
        renderUI(transformedProducts, rawProducts = null) {
          const products = rawProducts || transformedProducts;
          
          if (products.length <= 0) return;

          const productCompleteSetContainer = this.querySelector('.product-complete-set__container');
        
          if (productCompleteSetContainer) {
            let html = '';
            let noOfCompleteSetRendered = 0
            products.forEach(product => {
              if(noOfCompleteSetRendered < 4) {
                const product_image = this.changeImageUrlSize(product.featured_image, 'medium');
                html += `<img src="${product_image}" alt="${product.title}" loading="lazy">`;
                noOfCompleteSetRendered++;
              }
            });
            productCompleteSetContainer.innerHTML = html;
            this.style.removeProperty('display');
          }
          
          const addOnsItemsContainer = this.querySelector('.add-ons-items');
          if (addOnsItemsContainer) {
            let addOnsMobileImagesHtml = '';
            let html = '';
    
            transformedProducts.forEach((transformedProduct, index) => {
              const rawProduct = products[index];
              const product_image = this.changeImageUrlSize(rawProduct.featured_image);
              const formattedPrice = formatMoney(this.moneyWithoutCurrency(rawProduct.price));
              const formattedCompareAtPrice = formatMoney(this.moneyWithoutCurrency(rawProduct.compare_at_price));
            
            // Escape special characters for HTML attribute
            const productTags = String(transformedProduct.tags || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
            const variantId = transformedProduct.id;

              html += `
              <div class="add-ons-item">
                <div class="add-ons-items__checkbox-container" data-variant_id="${variantId}">
                  <input type="checkbox" 
                        class="add-ons-items__checkbox" 
                        data-product_price="${rawProduct.price}" 
                        data-product_id="${variantId}"
                        data-product_title="${rawProduct.title}"
                        data-product_tags="${productTags}">
                </div>
              `;

              // html += window.innerWidth > 749 ? `
              //   <div class="add-ons-items__img">
              //       <a href="/products/${product.handle}" class="add-ons-items__img-link"></a>
              //       <img src="${product_image}" alt="${product.title}" loading="lazy">
              //   </div>
              // ` : ``;

              html += `
                <div class="add-ons-items__info">
                    <p class="add-ons-items-title">${rawProduct.title}</p>
                    <div class="add-ons-items-price">
                      <div class="price price--large ${rawProduct.compare_at_price ? 'price--on-sale' : ''}">
                        <div class="price__container">
                          <div class="price__regular">
                            <span class="visually-hidden visually-hidden--inline">Regular price</span>
                            <span class="price-item price-item--regular">${formattedPrice}</span>
                          </div>
                          <div class="price__sale">
                            <span class="visually-hidden visually-hidden--inline">Regular price</span>
                            <span><s class="price-item price-item--regular">${formattedCompareAtPrice}</s></span>
                            <span class="visually-hidden visually-hidden--inline">Sale price</span>
                            <span class="price-item price-item--sale price-item--last">${formattedPrice}</span>
                          </div>
                          <small class="unit-price caption hidden">
                            <span class="visually-hidden">Unit price</span>
                            <span class="price-item price-item--last">
                              <span></span>
                              <span aria-hidden="true">/</span>
                              <span class="visually-hidden">&nbsp;per&nbsp;</span>
                              <span></span>
                            </span>
                          </small>
                        </div>
                      </div>
                    </div>
                </div>
              </div>`;
          
              let mobileImageSize = window.innerWidth > 749 ? (window.innerWidth > 1920 ? 'large' : 'medium') : 'small';

              addOnsMobileImagesHtml += `
                <div class="add-ons-item" data-product_id="${variantId}">
                  <div class="add-ons-items__img">
                    <a href="/products/${rawProduct.handle}" class="add-ons-items__img-link"></a>
                    <img src="${this.changeImageUrlSize(rawProduct.featured_image, 'medium')}" alt="${rawProduct.title}" loading="lazy" sizes="(min-width: 100px) 100px, 100vw" >
                  </div>
                </div>
              `;
            });
    
            addOnsItemsContainer.innerHTML = html;

            // Create and prepend notification to complete-set-component
            const notificationHtml = `<div class="add-ons-items__notification">
              <div class="add-ons-items__notification-content">
                <span class="svg-wrapper">
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M6.04074 0.453979C5.40811 0.453979 4.80139 0.705292 4.35405 1.15263C3.90671 1.59997 3.6554 2.20669 3.6554 2.83932V3.12054H2.06851L1.68184 8.94581C1.65843 9.2892 1.70588 9.63373 1.82122 9.95801C1.93657 10.2823 2.11735 10.5794 2.35234 10.8309C2.58734 11.0824 2.87153 11.2828 3.18726 11.4199C3.50299 11.5569 3.84352 11.6275 4.18771 11.6274H7.89378C8.23751 11.6275 8.5776 11.557 8.89297 11.4203C9.20834 11.2835 9.49228 11.0835 9.7272 10.8326C9.96212 10.5817 10.143 10.2852 10.2587 9.96149C10.3744 9.6378 10.4223 9.29381 10.3996 8.95083L10.013 3.12557H8.42609V2.83932C8.42609 2.52608 8.36439 2.2159 8.24451 1.92649C8.12464 1.63709 7.94894 1.37413 7.72744 1.15263C7.50594 0.931131 7.24298 0.755428 6.95357 0.635553C6.66417 0.515678 6.35399 0.453979 6.04074 0.453979ZM7.92391 2.83932V3.12054H4.15757V2.83932C4.15757 2.33988 4.35598 1.86089 4.70914 1.50772C5.0623 1.15456 5.5413 0.956157 6.04074 0.956157C6.54019 0.956157 7.01918 1.15456 7.37234 1.50772C7.72551 1.86089 7.92391 2.33988 7.92391 2.83932ZM7.92391 3.62272H4.15757V3.90394C4.15757 4.40339 4.35598 4.88238 4.70914 5.23554C5.0623 5.5887 5.5413 5.78711 6.04074 5.78711C6.54019 5.78711 7.01918 5.5887 7.37234 5.23554C7.72551 4.88238 7.92391 4.40339 7.92391 3.90394V3.62272ZM3.6554 3.90394V3.62272H2.54056L2.17899 8.98096C2.16039 9.25593 2.19857 9.53178 2.29115 9.79136C2.38374 10.0509 2.52875 10.2887 2.71716 10.4898C2.90557 10.691 3.13336 10.8512 3.38635 10.9605C3.63933 11.0698 3.91211 11.1259 4.18771 11.1253H7.89378C8.16895 11.1252 8.44118 11.0686 8.69357 10.959C8.94597 10.8494 9.17314 10.689 9.36102 10.488C9.54889 10.2869 9.69345 10.0494 9.78573 9.79018C9.87801 9.53094 9.91604 9.25551 9.89747 8.98096L9.54092 3.62272H8.42609V3.90394C8.42609 4.53657 8.17478 5.1433 7.72744 5.59064C7.2801 6.03798 6.67338 6.28929 6.04074 6.28929C5.40811 6.28929 4.80139 6.03798 4.35405 5.59064C3.90671 5.1433 3.6554 4.53657 3.6554 3.90394Z" fill="black"/>
                    </svg>
                </span>
                <p>1 item added</p>
              </div>
            </div>`;
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = notificationHtml;

            if (this.querySelector('.add-ons-items__notification')) {
              this.querySelector('.add-ons-items__notification').remove();
            }

            const notification = tempDiv.firstElementChild;
            this.insertBefore(notification, this.firstChild);

            this.querySelector('.add-ons-items--mobile__images').innerHTML = addOnsMobileImagesHtml;
            const completeSetElement = document.querySelector('.complete-set');
            if (completeSetElement) {
              completeSetElement.style.display = 'flex';
            }
            
            // Set initial mobile images visibility
            this.toggleMobileImagesVisibility();
          }
        }
    
        setupEventListeners() {
          // Checkbox event listeners
          this.querySelectorAll('.add-ons-items__checkbox').forEach(checkbox => {
            checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));
            // Prevent checkbox click from bubbling to the container
            checkbox.addEventListener('click', (e) => {
              e.stopPropagation();
            });
          });

          // Make entire .add-ons-item container clickable (desktop version)
          this.querySelectorAll('.add-ons-items .add-ons-item').forEach(item => {
            item.addEventListener('click', (e) => {
              // Don't trigger if clicking the link, button, or checkbox
              if (e.target.closest('.add-ons-items__img-link') || 
                  e.target.closest('.add-ons-item__cart-button') ||
                  e.target.closest('.add-ons-items__checkbox') ||
                  e.target.tagName === 'A') {
                return;
              }
              
              const checkbox = item.querySelector('.add-ons-items__checkbox');
              if (checkbox && !checkbox.disabled && !this.isLoading) {
                checkbox.checked = !checkbox.checked;
                checkbox.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
          });

          // Make mobile images clickable and sync with desktop checkboxes
          const mobileImagesContainer = this.querySelector('.add-ons-items--mobile__images');
          if (mobileImagesContainer) {
            mobileImagesContainer.querySelectorAll('.add-ons-item').forEach(mobileItem => {
              const link = mobileItem.querySelector('.add-ons-items__img-link');
              
              // Handle clicks on the mobile item to toggle checkbox
              mobileItem.addEventListener('click', (e) => {
                // Prevent link navigation - clicking toggles checkbox instead
                if (link) {
                  e.preventDefault();
                }
                
                const productId = mobileItem.dataset.product_id;
                if (!productId || this.isLoading) return;
                
                // Find corresponding desktop checkbox
                const desktopCheckbox = this.querySelector(`.add-ons-items__checkbox[data-product_id="${productId}"]`);
                if (desktopCheckbox && !desktopCheckbox.disabled) {
                  desktopCheckbox.checked = !desktopCheckbox.checked;
                  desktopCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
                }
              });
            });
          }

          // Button event listener
          const button = this.querySelector('.add-ons-item__cart-button');
          if (button) {
            button.addEventListener('click', this.handleButtonClick.bind(this));
          }
        }
    
        handleCheckboxChange(event) {
          const checkbox = event.target;
          
          // Prevent changes during loading or if checkbox is disabled
          if (this.isLoading || checkbox.disabled) {
            event.preventDefault();
            return;
          }
          
          const productId = checkbox.dataset.product_id;
          const product = this.state.products.find(p => p.id === productId);
          
          if (product) {
            product.checked = checkbox.checked;
            this.refreshUI();
          }
        }
    
        async handleButtonClick(event) {
          event.preventDefault();
          
          // Prevent multiple clicks during loading
          if (this.isLoading) {
            return;
          }
          
          // Backup current state before operation
          const previousState = this.backupCurrentState();
          
          // IMMEDIATELY disable all checkboxes and button to prevent any user interaction
          this.setLoadingState(true);
          
          try {
            if (this.state.hasAnyCompleteSetItemsInCart) {
              await this.smartUpdateCart();
            } else {
              await this.addToCart();
            }
          } catch (error) {
            console.error('Cart operation failed:', error);
            // Silently revert to previous state instead of showing error
            this.restoreState(previousState);
          } finally {
            // Remove loading state - re-enable based on current state
            this.setLoadingState(false);
          }
        }
    
        changeImageUrlSize(url, size="large") {
          return url.replace(/\.(jpg|jpeg|png|gif|webp)/, `_${size}.$1`);
        }
      
        moneyWithoutCurrency(price) {
          return (price / 100).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        }
    
        async addToCart() {
          const checkedItems = this.getCheckedItems();
          const itemCount = checkedItems.length;
          
          if (checkedItems.length === 0) return;
          
          this.hideNotification();
          
          try {
            const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: checkedItems })
            });
            
        if (response.ok) {
          const cartData = await response.json();
          
          // Update state to match what's now in cart
          this.state.products.forEach(product => {
            product.inCart = product.checked;
          });
          
          // Update initial cart state and flags
          this.state.initialCartState = this.state.products
            .filter(p => p.inCart)
            .map(p => p.id);
          this.state.hasAnyCompleteSetItemsInCart = true;
              
              // Update UI without overriding checkbox states
              this.updateUI(false); // Don't override checkbox states
              
              // Show specific add message
              const message = itemCount === 1 ? '1 item added' : `${itemCount} items added to basket`;
              this.showNotification('add', itemCount, message);
              
              // Refresh cart drawer with our source identifier
              const cartDrawer = document.querySelector('cart-drawer');
              if (cartDrawer && typeof cartDrawer.refresh === 'function') {
                cartDrawer.refresh(false, 'complete-set');
              }
            } else {
              throw new Error('Failed to add items to basket');
            }
          } catch (error) {
            console.error('Add to basket failed:', error);
          }
        }
    
      async smartUpdateCart() {
        const currentCheckedItems = this.state.products.filter(p => p.checked);
        const currentCartItems = this.state.products.filter(p => p.inCart);
        
        // Determine operations needed
        const itemsToAdd = currentCheckedItems.filter(p => !p.inCart);
        const itemsToRemove = currentCartItems.filter(p => !p.checked);
        
        if (itemsToAdd.length === 0 && itemsToRemove.length === 0) {
          return; // No changes needed
        }
        
        this.hideNotification();
        
        try {
          // Handle removals first if any
          if (itemsToRemove.length > 0) {
            // Fetch current cart to get quantities
            const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
            const cart = await cartResponse.json();
            
            const updates = {};
            itemsToRemove.forEach(item => {
              // Find current quantity in cart
              const cartItem = cart.items.find(ci => ci.variant_id.toString() === item.id);
              const currentQty = cartItem ? cartItem.quantity : 0;
              
              updates[item.id] = Math.max(0, currentQty - 1);
            });
            
            const removeResponse = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates })
            });
            
            if (!removeResponse.ok) {
              throw new Error('Failed to remove items from cart');
            }
          }
          
          // Handle additions with properties if any
          if (itemsToAdd.length > 0) {
            // Build items array with delivery cost properties
            const itemsWithProperties = itemsToAdd.map(item => {
              // Get tags from checkbox or fallback to state
              const checkbox = this.querySelector(`[data-product_id="${item.id}"]`);
              const productTags = checkbox?.dataset.product_tags || item.tags;
              
              // Calculate delivery cost if delivery_tabs is loaded
              const lowestDeliveryCost = productTags && 
                typeof delivery_tabs !== 'undefined' && 
                delivery_tabs.courierData?.length > 0
                  ? delivery_tabs.calculateLowestDeliveryCost(productTags)
                  : null;
              
              const addItem = { id: item.id, quantity: 1 };
              
              if (lowestDeliveryCost !== null) {
                addItem.properties = { 'Lowest Delivery Cost': String(lowestDeliveryCost) };
              }
              
              return addItem;
              });
          
            const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: itemsWithProperties })
            });
            
            if (!addResponse.ok) {
              throw new Error('Failed to add items to cart');
            }
          }
          
            // Update internal state without fetching
            this.state.products.forEach(product => {
              product.inCart = product.checked;
            });
            
            // Update initial cart state
            this.state.initialCartState = this.state.products
              .filter(p => p.inCart)
              .map(p => p.id);
          
          // Update hasAnyCompleteSetItemsInCart flag
          this.state.hasAnyCompleteSetItemsInCart = this.state.initialCartState.length > 0;
          
          // Update UI without overriding checkbox states
          this.updateUI(false); // Don't override checkbox states
            
            // Generate detailed operation message
            const message = this.generateOperationMessage(itemsToAdd.length, itemsToRemove.length);
            this.showNotification('update', itemsToAdd.length + itemsToRemove.length, message);
            
            // Refresh cart drawer with our source identifier
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.refresh === 'function') {
              cartDrawer.refresh(false, 'complete-set');
            }
          } catch (error) {
            console.error('Smart update failed:', error);
          }
        }
    
        async checkInitialCartState() {
          try {
            const response = await fetch(`${window.Shopify.routes.root}cart.js`);
            const cart = await response.json();
            this.syncWithCart(cart, true); // true = initial load
          } catch (error) {
            console.error('Failed to check initial cart state:', error);
          }
        }
    
        handleCartUpdate(event) {
          // Only sync with cart if the update came from external sources
          if (event && event.source === 'complete-set') {
            return; // Ignore our own updates
          }
          
          if (this.cartSyncTimeout) {
            clearTimeout(this.cartSyncTimeout);
          }
          
          this.cartSyncTimeout = setTimeout(() => {
            fetch(`${window.Shopify.routes.root}cart.js`)
              .then(response => response.json())
              .then(cart => this.syncWithCart(cart, false)) // false = not initial load
              .catch(error => console.error('Cart sync failed:', error))
              .finally(() => {
                this.cartSyncTimeout = null;
              });
          }, 300);
        }
    
      syncWithCart(cart, isInitialLoad = false) {
        const cartVariantIds = new Set(
          cart.items
            .filter(item => item.quantity > 0)
            .map(item => item.variant_id.toString())
        );
        
        this.state.initialCartState = this.state.products
          .filter(p => cartVariantIds.has(p.id))
          .map(p => p.id);
        
        // If no items are in cart, select all checkboxes
        const hasNoItemsInCart = this.state.initialCartState.length === 0;
        
        this.state.products.forEach(product => {
          product.inCart = cartVariantIds.has(product.id);
          
          // Only force checkbox state on initial load
          if (isInitialLoad) {
            product.checked = hasNoItemsInCart ? true : product.inCart;
          }
        });
        
        this.state.hasAnyCompleteSetItemsInCart = this.state.initialCartState.length > 0;
        
        this.updateUI(!isInitialLoad ? false : true);
      }
      
        // Change detection logic - key for button state
        hasSelectionChanged() {
          const currentCheckedIds = this.state.products
            .filter(p => p.checked)
            .map(p => p.id)
            .sort();
          
          const currentCartIds = this.state.products
            .filter(p => p.inCart)
            .map(p => p.id)
            .sort();
          
          if (currentCheckedIds.length !== currentCartIds.length) return true;
          
          return !currentCheckedIds.every((id, index) => id === currentCartIds[index]);
        }
    
        getCheckedItems() {
          return this.state.products
            .filter(p => p.checked && !p.inCart)
            .map(p => {
              // Get tags from checkbox or fallback to state
              const checkbox = this.querySelector(`[data-product_id="${p.id}"]`);
              const productTags = checkbox?.dataset.product_tags || p.tags;
              
              // Calculate delivery cost
              const lowestDeliveryCost = productTags && 
                typeof delivery_tabs !== 'undefined' && 
                delivery_tabs.courierData?.length > 0
                  ? delivery_tabs.calculateLowestDeliveryCost(productTags)
                  : null;
              
              const item = { id: p.id, quantity: 1 };
              
              if (lowestDeliveryCost !== null) {
                item.properties = { 'Lowest Delivery Cost': String(lowestDeliveryCost) };
              }
              
              return item;
            });
        }
    
        backupCurrentState() {
          // Create a deep copy of current state for restoration if operation fails
          return {
            products: this.state.products.map(product => ({
              id: product.id,
              checked: product.checked,
              inCart: product.inCart
            })),
            initialCartState: [...this.state.initialCartState],
            hasAnyCompleteSetItemsInCart: this.state.hasAnyCompleteSetItemsInCart,
            totals: { ...this.state.totals },
            buttonText: this.state.buttonText
          };
        }
    
        restoreState(previousState) {
          // Restore the previous state silently
          this.state.products = previousState.products;
          this.state.initialCartState = previousState.initialCartState;
          this.state.hasAnyCompleteSetItemsInCart = previousState.hasAnyCompleteSetItemsInCart;
          this.state.totals = previousState.totals;
          this.state.buttonText = previousState.buttonText;
          
          // Update UI to reflect restored state
          this.updateUI();
        }
    
        syncInternalStateWithCheckboxes() {
          // Sync our internal state with what the user has actually selected
          this.state.products.forEach(product => {
            const checkbox = this.querySelector(`[data-product_id="${product.id}"]`);
            if (checkbox) {
              product.checked = checkbox.checked;
            }
          });
        }
    
        updateTotals() {
          const checkedProducts = this.state.products.filter(p => p.checked);
          this.state.totals.itemCount = checkedProducts.length;
          this.state.totals.totalPrice = checkedProducts.reduce((sum, p) => sum + p.price, 0);
          
          // Update DOM elements
          const totalElement = this.querySelector('.add-ons-item__total');
          const priceElement = this.querySelectorAll('.add-ons-item__total-price');
          
          if (totalElement) {
            totalElement.textContent = `Total (${this.state.totals.itemCount} Item/s)`;
          }
          
          if (priceElement.length > 0) {
            priceElement.forEach(element => {
              element.textContent = formatMoney(this.moneyWithoutCurrency(this.state.totals.totalPrice));
            });
          }
        }
    
        updateButtonState() {
          const button = this.querySelector('.add-ons-item__cart-button');
          if (!button) return;
          
          const buttonState = this.getButtonState();
          this.state.buttonText = buttonState.text;
          button.disabled = buttonState.disabled;
          
          const buttonText = button.querySelector('span');
          if (buttonText) {
            buttonText.textContent = this.state.buttonText;
          }
        }
    
        getButtonState() {
          const button = this.querySelector('.add-ons-item__cart-button');
          const dynamicButtonText = button?.dataset?.buttonText || 'ADD TO BASKET';
          
          const hasChecked = this.state.products.some(p => p.checked);
          const hasInCart = this.state.products.some(p => p.inCart);
          const hasChanges = this.hasSelectionChanged();
          
          // No items checked, no items in cart
          if (!hasChecked && !hasInCart) {
            return { text: dynamicButtonText, disabled: true };
          }
          
          // No items checked, but items in cart (remove all scenario)
          if (!hasChecked && hasInCart) {
            return { text: 'UPDATE ITEMS IN BASKET', disabled: false };
          }
          
          // Items checked, no items in cart (fresh add)
          if (hasChecked && !this.state.hasAnyCompleteSetItemsInCart) {
            return { text: dynamicButtonText, disabled: false };
          }
          
          // Items checked, items in cart (update scenario)
          return { text: 'UPDATE ITEMS IN BASKET', disabled: !hasChanges };
        }
    
        generateOperationMessage(addedCount, removedCount) {
          const messages = [];
          
          if (addedCount > 0) {
            messages.push(addedCount === 1 ? '1 item added' : `${addedCount} items added`);
          }
          
          if (removedCount > 0) {
            messages.push(removedCount === 1 ? '1 item removed' : `${removedCount} items removed`);
          }
          
          return messages.join(', ');
        }
    
        refreshUI() {
          // Quick update for totals and button state only (used during interactions)
          this.updateTotals();
          this.updateButtonState();
          this.toggleMobileImagesVisibility();
        }
        
        toggleMobileImagesVisibility() {
          // Toggle visibility of mobile images based on checkbox state
          const mobileImagesContainer = this.querySelector('.add-ons-items--mobile__images');
          if (!mobileImagesContainer) return;
          
          mobileImagesContainer.querySelectorAll('.add-ons-item').forEach(mobileItem => {
            const productId = mobileItem.dataset.product_id;
            if (!productId) return;
            
            // Find corresponding checkbox
            const checkbox = this.querySelector(`.add-ons-items__checkbox[data-product_id="${productId}"]`);
            if (checkbox) {
              if (checkbox.checked) {
                mobileItem.classList.remove('add-ons-item--hidden');
                mobileItem.classList.add('add-ons-item--visible');
              } else {
                mobileItem.classList.remove('add-ons-item--visible');
                mobileItem.classList.add('add-ons-item--hidden');
              }
            }
          });
        }
    
        updateUI(syncCheckboxes = true) {
          // Full UI update including checkboxes (used after cart operations)
          this.querySelectorAll('.add-ons-items__checkbox').forEach(checkbox => {
            const productId = checkbox.dataset.product_id;
            const product = this.state.products.find(p => p.id === productId);
            
            if (product) {
              // Only sync checkbox state if explicitly requested (not when coming out of loading)
              if (syncCheckboxes) {
                checkbox.checked = product.checked;
              }
              // Checkboxes stay enabled unless in loading state
              if (!this.isLoading) {
                checkbox.disabled = false;
              }
            }
          });

          this.refreshUI();
        }
    
        setLoadingState(loading) {
          this.isLoading = loading;
          
          const button = this.querySelector('.add-ons-item__cart-button');
          const spinner = button?.querySelector('.loading__spinner');
          const checkboxes = this.querySelectorAll('.add-ons-items__checkbox');
          
          if (loading) {
            // Disable button and show spinner
            if (button) {
              button.disabled = true;
              button.setAttribute('aria-busy', 'true');
            }
            if (spinner) {
              spinner.classList.remove('hidden'); // Show spinner by removing hidden class
            }
            
            // Disable all checkboxes during operation
            checkboxes.forEach(checkbox => {
              checkbox.disabled = true;
              checkbox.setAttribute('aria-busy', 'true');
            });
            
            // Add loading class to component for styling
            this.classList.add('loading');
            
          } else {
            // Re-enable based on current state
            if (button) {
              button.removeAttribute('aria-busy');
              // Button state will be set by updateButtonState()
            }
            if (spinner) {
              spinner.classList.add('hidden'); // Hide spinner by adding hidden class
            }
            
            // Re-enable all checkboxes
            checkboxes.forEach(checkbox => {
              checkbox.disabled = false;
              checkbox.removeAttribute('aria-busy');
            });
            
            // Remove loading class
            this.classList.remove('loading');
            
            // Update UI but don't override user checkbox changes
            this.updateUI(false);
          }
        }

        showNotification(operation, itemCount = 1, customMessage = null) {
  
          //scroll to top of the addons items when showing notification
          const addonsItemsContainer = this.querySelector('.add-ons-items');
          if (addonsItemsContainer) {
            addonsItemsContainer.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }
        
          const notification = this.querySelector('.add-ons-items__notification');
          const notificationContent = this.querySelector('.add-ons-items__notification-content');
          const notificationText = notificationContent?.querySelector('p');
          
          if (!notification || !notificationContent || !notificationText) return;
          
          // Set notification text based on operation
          if (operation === 'add') {
            notificationText.textContent = customMessage || (itemCount === 1 ? '1 item added' : 'Items added to cart');
          } else if (operation === 'update') {
            notificationText.textContent = customMessage || 'Updated items in cart';
          }
          
          // Show notification with animation
          notification.style.display = 'flex';
          
          // Force reflow to ensure display change is applied before animation
          notification.offsetHeight;
          
          // Trigger fade in and slide animation
          notification.style.opacity = '1';
          notification.style.transform = 'translateY(0)';
        
          // Hide notification after 2 seconds
          setTimeout(() => {
            this.hideNotification();
          }, 2100);
        }
        
        hideNotification() {
          const notification = this.querySelector('.add-ons-items__notification');
          if (!notification) return;
          
          // Start fade out and slide up animation
          notification.style.opacity = '0';
          
          // Wait for animation to complete before hiding
          setTimeout(() => {
            notification.style.display = 'none';
          notification.style.transform = 'translateY(20px)';
        }, 300); // Match this to your CSS transition duration
        }
    
    
        // localStorage methods
        mergeProducts(apiProducts, storedProducts) {
          // Create a map of stored products by variant ID for quick lookup
          const storedMap = new Map();
          storedProducts.forEach(product => {
            const variantId = product.variants?.[0]?.id || product.id;
            storedMap.set(variantId.toString(), product);
          });
          
          // Start with API products (these are fresh)
          const mergedMap = new Map();
          apiProducts.forEach(product => {
            const variantId = product.variants[0].id.toString();
            mergedMap.set(variantId, product);
          });
          
          // Add any stored products that aren't in the API results
          storedProducts.forEach(product => {
            const variantId = product.variants?.[0]?.id?.toString() || product.id?.toString();
            if (variantId && !mergedMap.has(variantId)) {
              mergedMap.set(variantId, product);
            }
          });
          
          return Array.from(mergedMap.values());
        }
    
        storeProducts(storageKey, products, mainProductId) {
          try {
            const storageData = {
              products,
              mainProductId,
              timestamp: Date.now(),
              version: '1.0'
            };
            localStorage.setItem(storageKey, JSON.stringify(storageData));
          } catch (error) {
            console.warn('Failed to store complete set products:', error);
          }
        }
    
        getStoredProducts(storageKey, currentProductId) {
          try {
            const data = localStorage.getItem(storageKey);
            if (!data) return [];
            
            const parsed = JSON.parse(data);
            if (!this.isValidCacheData(parsed, currentProductId)) return [];
            
            return parsed.products || parsed; // Handle both new and legacy formats
          } catch (error) {
            return [];
          }
        }
    
        isValidCacheData(parsed, currentProductId) {
          // Handle legacy format (just array of products)
          if (Array.isArray(parsed)) return true;
          
          // Handle new format with metadata
          if (!parsed.products || !Array.isArray(parsed.products)) return false;
          
          // Validate product ID match
          if (parsed.mainProductId && parsed.mainProductId !== currentProductId) return false;
          
          // Check expiration (7 days)
          if (parsed.timestamp) {
            const maxAge = 24 * 60 * 60 * 1000; // 24 hours
            if (Date.now() - parsed.timestamp > maxAge) {
              // Clean up expired data
              return false;
            }
          }
          
          return true;
        }
    
        transformProduct(product) {
          const tagsString = Array.isArray(product.tags) ? product.tags.join(', ') : (product.tags || '');
          
          return {
            id: product.variants[0].id.toString(),
            title: product.title,
            price: product.price,
            handle: product.handle,
            image: product.featured_image,
            compareAtPrice: product.compare_at_price,
            tags: tagsString,
            checked: false,
            inCart: false
          };
        }
      
        async initializeWithProducts(products) {
          this.state.products = products.map(product => this.transformProduct(product));
    
          this.renderUI(this.state.products, products);
          this.setupEventListeners();
        
          this.dispatchEvent(new CustomEvent('add-ons-content-updated', {
            bubbles: true
          }));
          
          await this.checkInitialCartState();
        }
    
        disconnectedCallback() {
            if (this.boundCartHandler) {
                document.removeEventListener('cart:recommendations-updated', this.boundCartHandler);
            }
        
            if (this.cartUpdateUnsubscriber) {
              this.cartUpdateUnsubscriber();
            }
            
            if (this.boundResizeHandler) {
                window.removeEventListener('resize', this.boundResizeHandler);
            }
            
            // Clear any pending cart sync timeout
            if (this.cartSyncTimeout) {
              clearTimeout(this.cartSyncTimeout);
            }
        }
      
    }
  )
}